import React, { FC, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Screen, Spacing } from '../../../theme';
import { useClickOutside } from '../../../utils';
import { Button, H1 } from '../../ui';
import { CloseIcon } from '../../ui/icons';
import { FlexContainer } from '../Container';
import { Description } from '../InputText/style';
import Overlay from '../Overlay';
import { CloseIconContainer, ModalContainer, Root } from './style';

export interface ModalProps {
  children?: React.ReactNode;
  isOpen: boolean;
  close?: () => void;
  title?: string;
  maxWidth?: Screen;
  hideCloseButton?: boolean;
  padding?: Spacing;
  hideHeading?: boolean;
  noBorder?: boolean;
  description?: string;
}

const Modal: FC<ModalProps> = ({
  children,
  isOpen,
  title,
  close,
  maxWidth,
  hideCloseButton,
  padding,
  hideHeading,
  noBorder,
  description,
}) => {
  const contentRef = useRef(null);
  useClickOutside(
    contentRef,
    isOpen && !!close,
    () => {
      close?.();
    },
    (event: MouseEvent) => {
      const target = event.target as Element | null;
      return !!target?.closest('.react-datepicker, .react-datepicker-popper, .react-datepicker__portal');
    }
  );

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const stopRowActivation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return createPortal(
    <Overlay onClick={stopRowActivation} onMouseDown={stopRowActivation}>
      <Root onClick={stopRowActivation} onMouseDown={stopRowActivation}>
        <ModalContainer
          padding={padding}
          bgColor="white"
          bordered={!noBorder}
          maxWidth={maxWidth || 'sm'}
          ref={contentRef}
        >
          {!hideCloseButton && (
            <CloseIconContainer>
              <Button ghost onClick={close} shadow={false}>
                <CloseIcon />
              </Button>
            </CloseIconContainer>
          )}
          <FlexContainer flexDirection="column" gap="xs">
            {!hideHeading && (
              <FlexContainer padLeft={true} flexDirection="column" gap="xs">
                <H1>{title}</H1>
                {description && <Description>{description}</Description>}
              </FlexContainer>
            )}
            {children}
          </FlexContainer>
        </ModalContainer>
      </Root>
    </Overlay>,
    document.body
  );
};

export default Modal;
